import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { User, Mail, Phone, AtSign, Lock, Eye, EyeOff, ChevronRight, Shield, CheckCircle, ArrowLeft } from "lucide-react";
import { Button } from "./ui/button";



import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const UserSignupPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Basic Info, 2: Security Questions
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [securityQuestions, setSecurityQuestions] = useState([]);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    username: "",
    password: "",
    confirmPassword: "",
    question1: "",
    answer1: "",
    question2: "",
    answer2: "",
    question3: "",
    answer3: ""
  });

  // Fetch security questions on mount
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const res = await axios.get(`${API}/users/security-questions`);
        setSecurityQuestions(res.data.questions || []);
      } catch (e) {
        console.error("Error fetching security questions:", e);
      }
    };
    fetchQuestions();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const validateStep1 = () => {
    if (!formData.name.trim()) return "Name is required";
    if (!formData.email.trim()) return "Email is required";
    if (!/^\S+@\S+\.\S+$/.test(formData.email)) return "Invalid email format";
    if (!formData.phone.trim()) return "Phone number is required";
    if (!formData.username.trim()) return "Username is required";
    if (formData.username.length < 3) return "Username must be at least 3 characters";
    if (!formData.password) return "Password is required";
    if (formData.password.length < 8) return "Password must be at least 8 characters";
    if (formData.password !== formData.confirmPassword) return "Passwords do not match";
    return null;
  };

  const validateStep2 = () => {
    if (!formData.question1 || !formData.answer1.trim()) return "Please complete all security questions";
    if (!formData.question2 || !formData.answer2.trim()) return "Please complete all security questions";
    if (!formData.question3 || !formData.answer3.trim()) return "Please complete all security questions";
    if (formData.question1 === formData.question2 || formData.question1 === formData.question3 || formData.question2 === formData.question3) {
      return "Please select 3 different security questions";
    }
    return null;
  };

  const handleNextStep = () => {
    const error = validateStep1();
    if (error) {
      setError(error);
      return;
    }
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const error = validateStep2();
    if (error) {
      setError(error);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await axios.post(`${API}/users/signup`, {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        username: formData.username.trim(),
        password: formData.password,
        security_questions: [
          { question: formData.question1, answer: formData.answer1 },
          { question: formData.question2, answer: formData.answer2 },
          { question: formData.question3, answer: formData.answer3 }
        ]
      });

      // Store token and user info
      localStorage.setItem("userToken", res.data.token);
      localStorage.setItem("userData", JSON.stringify(res.data.user));
      
      // Redirect to home or account
      navigate("/");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create account. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Get available questions (exclude already selected)
  const getAvailableQuestions = (currentField) => {
    const selected = [formData.question1, formData.question2, formData.question3].filter(q => q && q !== formData[currentField]);
    return securityQuestions.filter(q => !selected.includes(q));
  };

  return (
    <main className="min-h-screen bg-[#050505]">
      
      
      
      <div className="pt-8 pb-16 px-4">
        <div className="max-w-md mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-zinc-900/50 border border-white/10 p-8"
          >
            {/* Back Button */}
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-zinc-500 hover:text-white text-sm mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-white" />
              </div>
              <h1 className="font-secondary text-2xl font-bold uppercase tracking-tight text-white">
                Create Account
              </h1>
              <p className="text-zinc-500 text-sm mt-2">
                {step === 1 ? "Enter your information" : "Set up security questions"}
              </p>
            </div>

            {/* Progress Indicator */}
            <div className="flex items-center justify-center gap-2 mb-8">
              <div className={`w-3 h-3 rounded-full ${step >= 1 ? 'bg-white' : 'bg-zinc-700'}`} />
              <div className={`w-12 h-0.5 ${step >= 2 ? 'bg-white' : 'bg-zinc-700'}`} />
              <div className={`w-3 h-3 rounded-full ${step >= 2 ? 'bg-white' : 'bg-zinc-700'}`} />
            </div>

            {error && (
              <div className="mb-6 p-3 bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {step === 1 ? (
                /* Step 1: Basic Info */
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
                      Full Name
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        className="w-full bg-zinc-800 border border-white/10 text-white pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-white/30"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
                      Email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        className="w-full bg-zinc-800 border border-white/10 text-white pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-white/30"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
                      Phone Number
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(555) 123-4567"
                        className="w-full bg-zinc-800 border border-white/10 text-white pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-white/30"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
                      Username
                    </label>
                    <div className="relative">
                      <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <input
                        type="text"
                        name="username"
                        value={formData.username}
                        onChange={handleChange}
                        placeholder="johndoe123"
                        className="w-full bg-zinc-800 border border-white/10 text-white pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-white/30"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Min 8 characters"
                        className="w-full bg-zinc-800 border border-white/10 text-white pl-10 pr-10 py-3 text-sm focus:outline-none focus:border-white/30"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <input
                        type={showPassword ? "text" : "password"}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        placeholder="Re-enter password"
                        className="w-full bg-zinc-800 border border-white/10 text-white pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-white/30"
                      />
                    </div>
                  </div>

                  <Button
                    type="button"
                    onClick={handleNextStep}
                    className="w-full bg-white text-black hover:bg-zinc-200 font-secondary font-bold uppercase tracking-widest text-sm py-3 h-auto rounded-none mt-6"
                  >
                    Continue
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              ) : (
                /* Step 2: Security Questions */
                <div className="space-y-5">
                  <div className="flex items-center gap-2 text-zinc-400 text-sm mb-4">
                    <Shield className="w-4 h-4" />
                    <span>Choose 3 security questions for account recovery</span>
                  </div>

                  {[1, 2, 3].map((num) => (
                    <div key={num} className="space-y-2">
                      <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400">
                        Security Question {num}
                      </label>
                      <select
                        name={`question${num}`}
                        value={formData[`question${num}`]}
                        onChange={handleChange}
                        className="w-full bg-zinc-800 border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-white/30"
                      >
                        <option value="">Select a question...</option>
                        {getAvailableQuestions(`question${num}`).map((q, idx) => (
                          <option key={idx} value={q}>{q}</option>
                        ))}
                        {formData[`question${num}`] && (
                          <option value={formData[`question${num}`]}>{formData[`question${num}`]}</option>
                        )}
                      </select>
                      <input
                        type="text"
                        name={`answer${num}`}
                        value={formData[`answer${num}`]}
                        onChange={handleChange}
                        placeholder="Your answer"
                        className="w-full bg-zinc-800 border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-white/30"
                      />
                    </div>
                  ))}

                  <div className="flex gap-3 mt-6">
                    <Button
                      type="button"
                      onClick={() => setStep(1)}
                      className="flex-1 bg-zinc-800 text-white hover:bg-zinc-700 font-secondary font-bold uppercase tracking-widest text-sm py-3 h-auto rounded-none"
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-white text-black hover:bg-zinc-200 font-secondary font-bold uppercase tracking-widest text-sm py-3 h-auto rounded-none"
                    >
                      {loading ? "Creating..." : "Create Account"}
                      <CheckCircle className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              )}
            </form>

            {/* Login Link */}
            <p className="text-center text-zinc-500 text-sm mt-6">
              Already have an account?{" "}
              <Link to="/login-user" className="text-white hover:underline">
                Sign In
              </Link>
            </p>
          </motion.div>
        </div>
      </div>

      
    </main>
  );
};

export default UserSignupPage;
